import React, { useCallback, useEffect, useState, useRef } from 'react';
import { FieldInputProps, useField } from 'formik';
import { debounce } from 'lodash';
import styled, { css } from 'styled-components';

import {
  DestinationOption,
  getReferencesAutocomplete,
  GroupedOptions,
  GroupName,
} from '@lt/api/dist/methods/references/types';

import { SearchType } from '../../types';
import { SEARCH_TYPE_NAMES, FIELD_NAMES } from '../../constants';
import { DestinationPickerProps } from '../DestinationPicker/types';
import { linkGroupDict } from '../DestinationPicker/constants';
import { addStep, useCigarette, Values } from '../../context';
import DestinationPicker from '../DestinationPicker';
import {
  afterDatepickerStep,
  afterDepartureStep,
  afterDestinationStep,
  isDestinationStep,
  isMetaregionsStep,
  isPopularCountriesStep,
  lastStep,
} from '../../utils';
import { isTouchScreen } from '../../../../utils/platformUtils';
import { InputActionMeta } from 'react-select/dist/declarations/src/types';

type FormDestinationProps = {
  fullOptions: GroupedOptions;
  startOptions: GroupedOptions;
  foundGroupedOptions: GroupedOptions;
  fetchAutocomplete: (params: getReferencesAutocomplete) => void;
  fetchPlaces: (departureLabel: string) => void;
  className?: string;
  searchType: SearchType;
  variant?: 'default' | 'hotel';
  isWidget?: boolean;
  isOpenInitial?: boolean;
  isLoading?: boolean;
};

export const FormDestinationPicker = ({
  name,
  fullOptions,
  startOptions,
  foundGroupedOptions,
  fetchAutocomplete,
  fetchPlaces,
  className,
  searchType,
  variant = 'default',
  isWidget = false,
  isOpenInitial = false,
  isLoading,
}: FieldInputProps<DestinationOption> & FormDestinationProps): JSX.Element | null => {
  const [menuIsOpenExceptWizard, setMenuIsOpenExceptWizard] = useState(isOpenInitial);
  const [options, setOptions] = useState<GroupedOptions>([]);

  const prevSearchTypeRef = useRef<string>(searchType);

  const [{ value }, { error }, { setValue, setError }] = useField<Values['destination']>(name);
  const [inputValue, setInputValue] = useState(value?.label);

  const [{ value: departureValue }, , { setValue: setDepartureValue }] = useField<
    Values['departure']
  >(FIELD_NAMES.departure);
  const [, , { setValue: setDatepickerValue }] = useField<Values['datepicker']>(
    FIELD_NAMES.datepicker
  );
  const [, , { setValue: setTouristsValue }] = useField<Values['tourists']>(FIELD_NAMES.tourists);

  const {
    state: { steps, isWizard, isMobile },
    dispatch,
  } = useCigarette();
  const isWizardOpen =
    searchType === SEARCH_TYPE_NAMES.PACKAGE
      ? isWizard && afterDepartureStep(steps)
      : isWizard && afterDatepickerStep(steps);

  const menuIsOpenWizard = afterDestinationStep(steps) && !isWizardOpen;
  const menuIsOpen = isWizard ? menuIsOpenWizard : menuIsOpenExceptWizard;

  const isTrip = searchType === SEARCH_TYPE_NAMES.TRIP;

  const setGroupOptionsByIds = useCallback(
    (groupIds: GroupName[]) => {
      const newGroupedOptions = fullOptions.filter(({ groupName }) =>
        groupIds.includes(linkGroupDict[groupName])
      );
      setOptions(newGroupedOptions);
    },
    [fullOptions]
  );

  const changeOptionsByIds = useCallback(
    (groupIds: GroupName[]) => {
      setGroupOptionsByIds(groupIds);

      if (isWizard) {
        dispatch(addStep(groupIds[0]));
      }
    },
    [dispatch, isWizard, setGroupOptionsByIds]
  );

  useEffect(() => {
    if (isPopularCountriesStep(steps) || isMetaregionsStep(steps)) {
      setGroupOptionsByIds([lastStep(steps) as GroupName]);
    }
    if (isDestinationStep(steps)) {
      setOptions(startOptions);
    }
  }, [setGroupOptionsByIds, startOptions, steps]);

  useEffect(() => {
    setOptions(startOptions);
  }, [startOptions]);

  useEffect(() => {
    fetchPlaces(departureValue?.value || '');
  }, [departureValue?.value, fetchPlaces]);

  useEffect(() => {
    setOptions(foundGroupedOptions);
  }, [foundGroupedOptions]);

  useEffect(() => {
    if (error) {
      openMenu();
    }
  }, [error]);

  /**
   * При переключении типа поиска на экскурсии нужно очистить поле, если
   * до этого в нём была опция другого типа поиска
   * И наоборот при переключении типа поиска с экскурсий на обычный
   * нужно очистить поле, т.к. иначе поиск будет не того формата
   */
  useEffect(() => {
    /* TODO: LT-37919 - разрулить некорректные данные на уровне стейта */
    if (
      searchType === SEARCH_TYPE_NAMES.TRIP ||
      prevSearchTypeRef.current === SEARCH_TYPE_NAMES.TRIP
    ) {
      setInputValue(undefined);
      setValue(undefined);
    }

    prevSearchTypeRef.current = searchType;
  }, [searchType]);

  const changeHandler = useCallback(
    debounce((newValue: string) => {
      if (newValue) {
        fetchAutocomplete({
          query: newValue,
          from_city: departureValue?.value,
        });
      }
    }, 100),
    [departureValue?.value]
  );

  const onInputChange = (currentValue: string, { action }: InputActionMeta) => {
    // if (!currentValue) {
    //   setOptions(startOptions);
    // }
    if (action === 'input-blur' && !isTouchScreen) {
      setInputValue(value ? value.label : '');
    }

    if (action === 'input-change') {
      setInputValue(currentValue);

      /**
       * Нужно, чтобы при очищении поля на десктопе в нём не оставалось
       * ранее выбранное значение
       */
      if (!currentValue) {
        setValue(undefined);
      }
    }

    changeHandler(currentValue);
  };

  const openMenu = () => {
    dispatch(addStep(name));
    setMenuIsOpenExceptWizard(true);
  };

  const closeMenu = () => {
    setOptions(startOptions);
    setMenuIsOpenExceptWizard(false);
    setError(undefined);
  };

  const onChange: DestinationPickerProps['onChange'] = (newValue, { action }) => {
    if (!newValue || (action === 'clear' && isMobile)) return;

    if (isWizard) {
      if (searchType === SEARCH_TYPE_NAMES.PACKAGE) {
        dispatch(addStep(FIELD_NAMES.departure));
        if (newValue.groupName === 'previousSearches') {
          dispatch(addStep(FIELD_NAMES.datepicker));
          dispatch(addStep(FIELD_NAMES.tourists));
        }
      } else if (searchType === SEARCH_TYPE_NAMES.HOTEL) {
        dispatch(addStep(FIELD_NAMES.datepicker));
        if (newValue.groupName === 'previousSearches') {
          dispatch(addStep(FIELD_NAMES.tourists));
        }
      }
    }

    switch (newValue.groupName) {
      case 'previousSearches': {
        const { destination, datepicker, tourists, departure } = newValue;
        setInputValue(destination.label);
        setValue(destination);
        setDepartureValue(departure);
        setDatepickerValue({
          left: {
            ...datepicker.left,
            date: new Date(datepicker.left.date || 0),
          },
          right: {
            ...datepicker.right,
            date: new Date(datepicker.right.date || 0),
          },
          shift: 0,
          buttonsClicked: datepicker.buttonsClicked || [],
          initialTab: datepicker.initialTab || null,
        });
        setTouristsValue(tourists);
        break;
      }
      default: {
        setValue(newValue);
        setInputValue(newValue ? newValue.label : '');
      }
    }
    setError(undefined);
    closeMenu();
  };

  if (variant === 'hotel') return null;

  return (
    <FormDestinationContainer $isWizard={isWizard} $isMobile={isMobile}>
      <DestinationPicker
        options={options}
        value={value}
        inputValue={inputValue}
        onInputChange={onInputChange}
        isMobile={isMobile}
        isWizardOpen={isWizardOpen}
        isWizard={isWizard}
        menuIsOpen={menuIsOpen}
        onMenuOpen={openMenu}
        isDisabled={isWizardOpen}
        onMenuClose={closeMenu}
        onChange={onChange}
        changeOptions={isTrip ? undefined : changeOptionsByIds}
        className={className}
        searchType={searchType}
        variant={variant}
        isWidget={isWidget}
        isLoading={isLoading}
      />
    </FormDestinationContainer>
  );
};

const FormDestinationContainer = styled.div<{
  $isWizard?: boolean;
  $isMobile?: boolean;
}>`
  ${({ $isMobile }) => $isMobile && 'width: 100%;'}
  ${({ $isWizard }) => !$isWizard && `flex: 1`};
  ${({ $isMobile, $isWizard }) =>
    $isMobile &&
    !$isWizard &&
    css`
      display: flex;
      flex-direction: column;
    `}
`;
